/* eslint-disable class-methods-use-this */

import Component from '@bythepixel/component-loader';

const DEFAULTS = {
  activeLinkClass: 'section-sub-nav__link--current',
  cssClassHidden: 'section-sub-nav__buttons--fade-down',
  cssClassVisible: 'section-sub-nav__buttons--fade-up',
  marker: 'section-sub-nav__marker',
  subNavLinks: 'section-sub-nav__link',
  sections: '[data-sub-nav-section]',
};

export default class SubNav extends Component {
  static selector = '[data-comp-sub-nav]';

  constructor(el, options = DEFAULTS) {
    super(el, options);
    this.anchorLinks = document.querySelectorAll('[data-anchor-link]');
    this.bottomStickyButton = document.querySelector('[data-sticky-bottom-sub-nav-button]');
    this.stickyButton = el.querySelector('[data-sticky-sub-nav-button]');
    this.linkContainer = el.querySelector('[data-sub-nav-links]');
    this.links = el.querySelectorAll(`.${this.$options.subNavLinks}`);
    this.marker = el.querySelector(`.${this.$options.marker}`);
    this.sections = document.querySelectorAll(this.$options.sections);
    this.currentLink = el.querySelector(`.${this.$options.activeLinkClass}`);

    this.init();
  }

  init() {
    this.pageLoadEvent();
    this.clickEvents();
    this.stickyButtonEvents();
    this.observeViewport();
    this.anchorLinkEvents(); // Must come after this.observeViewport()
  }

  /**
   * Due to a conflict with a scroll that happens inside of the observeViewport method
   * We must temporarily disable the the observer whenever other anchor links on the page are clicked
   */
  anchorLinkEvents() {
    if (this.anchorLinks.length === 0 || this.sections.length === 0) {
      return;
    }

    this.anchorLinks.forEach(link => {
      link.addEventListener('click', () => {
        this.sections.forEach(section => {
          this.sectionObserver.unobserve(section);
        });

        setTimeout(() => {
          this.sections.forEach(section => {
            this.sectionObserver.observe(section);
          });
        }, 500);
      });
    });
  }

  clickEvents() {
    if (this.links.length === 0) {
      return;
    }

    this.links.forEach(link => {
      link.addEventListener('click', e => {
        // Workaround for a bug in chrome, this prevents a scroll bug that is caused by anchor links and the intersection observerAPI
        e.preventDefault();
        const { href } = link;
        const hash = href.substring(href.indexOf('#'), href.length);
        const id = hash.substring(1, hash.length);
        window.history.replaceState(null, null, `${window.location.pathname}${hash}`);
        this.scrollToSectionAndHandleCurrentLink(document.getElementById(id), link);
      });
    });
  }

  events() {
    if (!this.currentLink) {
      return;
    }

    this.styleLinks();
    this.subNavScroll();
    this.moveMarker();
  }

  hideButton(el) {
    el.classList.remove(this.$options.cssClassVisible);
    el.classList.add(this.$options.cssClassHidden);
  }

  showButton(el) {
    el.classList.remove(this.$options.cssClassHidden);
    el.classList.add(this.$options.cssClassVisible);
  }

  moveMarker() {
    if (!this.marker || !this.currentLink) {
      return;
    }

    const { offsetLeft, offsetWidth } = this.currentLink;
    this.marker.style.left = `${offsetLeft}px`;
    this.marker.style.width = `${offsetWidth}px`;
  }

  observeViewport() {
    if (!this.linkContainer || this.sections.length === 0) {
      return;
    }

    const observeSections = entries => {
      const entry = entries[0];
      const sectionHeight = entry.boundingClientRect.height;

      // The second condition allows sections that are taller than the window viewport to be observed
      if (entry.intersectionRatio >= 1
        || (window.innerHeight < sectionHeight && entry.intersectionRatio > 0.5)) {
        const sectionId = entry.target.id;
        this.currentLink = this.linkContainer.querySelector(`[href="#${sectionId}"]`);
        this.events();
      }
    };

    this.sectionObserver = new IntersectionObserver(observeSections, {
      threshold: [0, 0.25, 0.5, 0.75, 1],
    });

    this.sections.forEach(section => {
      this.sectionObserver.observe(section);
    });
  }

  pageLoadEvent() {
    if (!this.linkContainer) {
      return;
    }

    const { hash } = window.location;
    const section = document.getElementById(hash.substring(1, hash.length));

    if (section) {
      this.scrollToSectionAndHandleCurrentLink(section, this.linkContainer.querySelector(`[href="${hash}"]`));
    }
  }

  getElementCoords(elem) {
    const box = elem.getBoundingClientRect();

    const { body } = document;
    const docEl = document.documentElement;

    const scrollTop = window.pageYOffset || docEl.scrollTop || body.scrollTop;
    const scrollLeft = window.pageXOffset || docEl.scrollLeft || body.scrollLeft;

    const clientTop = docEl.clientTop || body.clientTop || 0;
    const clientLeft = docEl.clientLeft || body.clientLeft || 0;

    const top = box.top + scrollTop - clientTop;
    const left = box.left + scrollLeft - clientLeft;

    return { top: Math.round(top), left: Math.round(left) };
  }

  scrollToSectionAndHandleCurrentLink(section, link) {
    const coords = this.getElementCoords(section);
    const subNavHeight = window.matchMedia('(min-width: 950px)').matches ? 103 : 52;

    window.scrollTo({
      top: coords.top - subNavHeight,
      behavior: 'smooth',
    });

    if (!link) {
      return;
    }

    setTimeout(() => {
      this.currentLink = link;
      this.events();
    }, 500); // 500ms allows this to run after the observer
  }

  stickyButtonEvents() {
    if (!this.bottomStickyButton || !this.stickyButton) {
      return;
    }

    const breakpoint = 768;
    const observeSections = entries => {
      const entry = entries[0];

      if (window.matchMedia(`(min-width: ${breakpoint}px)`).matches
        && entry.boundingClientRect.top > 0) {
        // In desktop and sub nav is sticking to the top
        this.hideButton(this.stickyButton);
      } else if (window.matchMedia(`(min-width: ${breakpoint}px)`).matches
        && entry.boundingClientRect.top <= 0) {
        // In desktop and sub nav is not sticking to the top
        this.showButton(this.stickyButton);
      } else if (window.matchMedia(`(max-width: ${breakpoint - 1}px)`).matches
        && entry.boundingClientRect.top > 0) {
        // In mobile and sub nav is sticking to the top
        this.hideButton(this.bottomStickyButton);
      } else if (window.matchMedia(`(max-width: ${breakpoint - 1}px)`).matches
        && entry.boundingClientRect.top <= 0) {
        // In mobile and sub nav is not sticking to the top
        this.showButton(this.bottomStickyButton);
      }
    };

    const observer = new IntersectionObserver(observeSections, {
      rootMargin: '0% 0% -100% 0%',
      threshold: [0, 0.25, 0.5, 0.75, 1],
    });

    observer.observe(this.$container);
  }

  styleLinks() {
    if (this.links.length === 0 || !this.currentLink) {
      return;
    }

    this.currentLink.classList.add(this.$options.activeLinkClass);
    this.links.forEach(link => {
      if (link !== this.currentLink && link.classList.contains(this.$options.activeLinkClass)) {
        link.classList.remove(this.$options.activeLinkClass);
      }
    });
  }

  subNavScroll() {
    if (!this.linkContainer || !this.currentLink) {
      return;
    }

    const { offsetWidth, offsetLeft } = this.currentLink;
    const linkContainerWidth = this.linkContainer.offsetWidth;
    const linkContainerWidthHalf = linkContainerWidth / 2;

    if (offsetLeft > linkContainerWidthHalf) {
      const linkCenter = (linkContainerWidth - offsetWidth) / 2;
      const scrollAmount = offsetLeft - linkCenter;
      this.linkContainer.scroll({
        left: scrollAmount,
        behavior: 'smooth',
      });
    } else {
      this.linkContainer.scrollTo(0, 0);
    }
  }
}
